import { SupplierInvoiceStatus, InvoiceStatus, Maybe } from '@bloomays-lib/types.shared';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { IconButton } from '@bloomays-lib/ui.shared';
import get from 'lodash/fp/get';
import { TFunction } from 'react-i18next';

const renderIconLabel = (label: string, emoji?: string, onClick?: () => void) => {
  return (
    <IconButton size="small" onClick={onClick} title={label} variant="contained">
      {emoji || label}
    </IconButton>
  ) as React.ReactNode;
};

const INPROGRESS_ICON = '🔄';
const CANCELLED_ICON = '❌';
const CREATED_TO_VALIDATE_ICON = '☑️';
const CREATED_AND_FINALIZED_ICON = '✅';
const ERROR_ICON = '🚨';
const NON_CREATED_ICON = '🚧';

export const getInvoiceStatusOptions = (
  translate: TFunction<['invoiceStatus'], undefined>,
): Record<InvoiceStatus, { value: InvoiceStatus; label: string }> => {
  return {
    'Non créée': {
      value: 'Non créée',
      label: `${NON_CREATED_ICON} ${translate('labelNonCreated', { ns: 'invoiceStatus' })}`,
    },
    'En erreur': { value: 'En erreur', label: `${ERROR_ICON} ${translate('labelError', { ns: 'invoiceStatus' })}` },
    'Créée à valider': {
      value: 'Créée à valider',
      label: `${CREATED_TO_VALIDATE_ICON} ${translate('labelCreatedToValidate', { ns: 'invoiceStatus' })}`,
    },
    'Créée et finalisée': {
      value: 'Créée et finalisée',
      label: `${CREATED_AND_FINALIZED_ICON} ${translate('labelCreatedAndFinalized', { ns: 'invoiceStatus' })}`,
    },
    'En cours': {
      value: 'En cours',
      label: `${INPROGRESS_ICON} ${translate('labelInProgress', { ns: 'invoiceStatus' })}`,
    },
    Annulée: { value: 'Annulée', label: `${CANCELLED_ICON} ${translate('labelCancelled', { ns: 'invoiceStatus' })}` },
  };
};

type ActivityInvoice = {
  bloomerInvoiceStatus: InvoiceStatus;
  bloomerInvoiceLastError: Maybe<string>;
  clientInvoiceStatus: InvoiceStatus;
  clientInvoiceLastError: Maybe<string>;
  supplierInvoiceStatus?: Maybe<SupplierInvoiceStatus>;
};

type BillingCustomerInvoice = {
  invoiceStatus: InvoiceStatus;
  lastCustomerInvoiceError?: string;
};

type InvoiceType = ActivityInvoice | BillingCustomerInvoice;

export const renderInvoiceStatusCell =
  <T extends InvoiceType>(
    rowColName: T extends ActivityInvoice ? 'bloomerInvoiceStatus' | 'clientInvoiceStatus' : 'invoiceStatus',
    rowErrorColName: T extends ActivityInvoice
      ? 'bloomerInvoiceLastError' | 'clientInvoiceLastError'
      : 'lastCustomerInvoiceError',
    couldBeCreated: (row: T) => boolean,
    translate: TFunction<['actitivyDashboard'], undefined>,
    clickHandler?: (row: T) => () => void,
  ) =>
  (params: GridRenderCellParams<T>): React.ReactNode => {
    // rendering for group header rows
    if (params.rowNode.type === 'group') {
      if (params.field !== '__row_group_by_columns_group__') return '';
      const groupingKey = params.rowNode.groupingKey as InvoiceStatus;
      if (!groupingKey) return;
      return getInvoiceStatusOptions(translate)[groupingKey].label;
    }
    const row = params.row as ActivityInvoice;
    const rowValue = get(rowColName, row);
    const rowErrorValue = get(rowErrorColName, row);
    switch (rowValue) {
      case 'Annulée':
        return renderIconLabel(translate('labelCancelled', { ns: 'invoiceStatus' }), CANCELLED_ICON);
      case 'Non créée':
        if (couldBeCreated(row as T)) {
          return renderIconLabel(
            translate('labelToCreate', { ns: 'invoiceStatus' }),
            NON_CREATED_ICON,
            clickHandler ? clickHandler(row as T) : undefined,
          );
        }
        return renderIconLabel(translate('labelNonCreated', { ns: 'invoiceStatus' }), NON_CREATED_ICON);
      case 'En erreur':
        return renderIconLabel(
          rowErrorValue || translate('labelError', { ns: 'invoiceStatus' }),
          ERROR_ICON,
          clickHandler ? clickHandler(row as T) : undefined,
        );
      case 'Créée à valider': {
        const importLabel =
          rowColName === 'bloomerInvoiceStatus' && row.supplierInvoiceStatus === 'imported'
            ? translate('labelImportedToValidate', { ns: 'invoiceStatus' })
            : translate('labelCreatedToValidate', { ns: 'invoiceStatus' });
        return renderIconLabel(
          `${importLabel}\n${rowErrorValue || ''}`,
          CREATED_TO_VALIDATE_ICON,
          clickHandler ? clickHandler(row as T) : undefined,
        );
      }
      case 'Créée et finalisée': {
        const importLabel =
          rowColName === 'bloomerInvoiceStatus' && row.supplierInvoiceStatus === 'imported'
            ? translate('labelImportedAndFinalized', { ns: 'invoiceStatus' })
            : translate('labelCreatedAndFinalized', { ns: 'invoiceStatus' });
        return renderIconLabel(
          importLabel,
          CREATED_AND_FINALIZED_ICON,
          clickHandler ? clickHandler(row as T) : undefined,
        );
      }
      case 'En cours':
        return renderIconLabel(translate('labelInProgress', { ns: 'invoiceStatus' }), INPROGRESS_ICON);
      default:
        return '';
    }
  };
